import { catalog, categories } from "@sobidogam/catalog";
import {
	classifyWithRules,
	findDuplicate,
	type HouseholdConsumptionState,
	isHomeItemCategory,
	normalizeStoreName,
	type RecurringPayment,
} from "@sobidogam/domain";
import type { Receipt } from "@sobidogam/storage";
import { useState } from "react";
import type { Draft } from "./App";
import { readStoreRules } from "./prefs";

interface StoreSuggestion {
	storeKey: string;
	storeDisplay: string;
	categoryId: string;
	lastAmount: number;
	visits: number;
}

/** Recent 30 days, most visited first. Computed in memory, never stored. */
function recentStores(
	receipts: Receipt[],
	now = Date.now(),
): StoreSuggestion[] {
	const byStore = new Map<string, StoreSuggestion & { lastAt: number }>();
	for (const receipt of receipts) {
		if (receipt.deletedAt || receipt.sensitiveMasked) continue;
		const at = Date.parse(receipt.transactedAt);
		if (!Number.isFinite(at) || now - at > 30 * 86_400_000) continue;
		const current = byStore.get(receipt.storeKey);
		if (!current) {
			byStore.set(receipt.storeKey, {
				storeKey: receipt.storeKey,
				storeDisplay: receipt.storeDisplay,
				categoryId: receipt.categoryId,
				lastAmount: receipt.totalAmount,
				visits: 1,
				lastAt: at,
			});
			continue;
		}
		current.visits += 1;
		if (at > current.lastAt) {
			current.lastAt = at;
			current.lastAmount = receipt.totalAmount;
			current.categoryId = receipt.categoryId;
			current.storeDisplay = receipt.storeDisplay;
		}
	}
	return [...byStore.values()]
		.sort((a, b) => b.visits - a.visits || b.lastAt - a.lastAt)
		.slice(0, 8);
}

function inferCategory(store: string): string {
	const guess = classifyWithRules({
		storeKey: normalizeStoreName(store).key,
		aiCategoryHint: null,
		catalog,
		rules: readStoreRules(),
	});
	return guess.categoryId === "etc-other" ? "" : guess.categoryId;
}

export function QuickRecord({
	receipts,
	household,
	onSave,
	recurring,
	initialCategoryId,
}: {
	receipts: Receipt[];
	household: HouseholdConsumptionState;
	onSave: (draft: Draft) => Promise<boolean>;
	recurring?: RecurringPayment;
	/** Set when the person came from a public category page (#44). */
	initialCategoryId?: string | undefined;
}) {
	const [store, setStore] = useState(recurring?.name ?? "");
	const [amount, setAmount] = useState(
		recurring ? String(recurring.amount) : "",
	);
	// No silent "기타": the category stays empty until it is guessed or chosen.
	const [categoryId, setCategoryId] = useState(
		recurring?.categoryId ?? initialCategoryId ?? "",
	);
	const [pinned, setPinned] = useState(
		Boolean(recurring) || Boolean(initialCategoryId),
	);
	const [itemName, setItemName] = useState("");
	const [items, setItems] = useState<string[]>([]);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	const [duplicateConfirmed, setDuplicateConfirmed] = useState(false);
	const suggestions = recentStores(receipts);
	const typed = normalizeStoreName(store).key;
	const matching = typed
		? suggestions.filter(
				(suggestion) =>
					suggestion.storeKey.includes(typed) && suggestion.storeKey !== typed,
			)
		: suggestions.slice(0, 5);
	const categoryName = (id: string) =>
		categories.find((category) => category.categoryId === id)?.nameKo ?? "기타";
	const numericAmount = Number(amount);
	const duplicate = findDuplicate(
		{
			receiptId: "new-quick-record",
			storeKey: normalizeStoreName(store).key,
			totalAmount: numericAmount,
			transactedAt: new Date().toISOString(),
		},
		receipts.filter((r) => !r.deletedAt),
	);
	const addItem = (name: string) => {
		const clean = name
			.normalize("NFKC")
			.trim()
			.replace(/\s+/g, " ")
			.slice(0, 80);
		if (clean && !items.includes(clean)) setItems([...items, clean]);
		setItemName("");
	};
	return (
		<form
			className="quick-record"
			onSubmit={async (event) => {
				event.preventDefault();
				if (busy) return;
				if (
					!store.trim() ||
					!Number.isSafeInteger(numericAmount) ||
					numericAmount <= 0
				) {
					setError("매장 이름과 1원 이상의 금액을 확인해 주세요.");
					return;
				}
				if (!categoryId) {
					setError("분류를 골라 주세요. 다음부터는 이 매장에 자동으로 붙어요.");
					return;
				}
				if (duplicate && !duplicateConfirmed) {
					setError("비슷한 기록이 있어요. 별개 결제인지 확인해 주세요.");
					return;
				}
				setBusy(true);
				setError("");
				const names = itemName.trim()
					? [...new Set([...items, itemName.trim()])]
					: items;
				try {
					const ok = await onSave({
						storeDisplay: store.trim(),
						totalAmount: numericAmount,
						categoryId,
						transactedAt: new Date().toISOString(),
						source: "manual",
						items: isHomeItemCategory(categoryId)
							? names.map((name) => ({ name, amount: null, qty: null }))
							: [],
						...(recurring ? { recurringPaymentId: recurring.id } : {}),
					});
					if (!ok)
						setError(
							"저장하지 못했어요. 입력은 남아 있으니 다시 시도해 주세요.",
						);
				} catch {
					setError("저장하지 못했어요. 입력은 남아 있으니 다시 시도해 주세요.");
				} finally {
					setBusy(false);
				}
			}}
		>
			{matching.length > 0 && !recurring && (
				<section className="recent-presets" aria-label="최근 매장으로 채우기">
					<p>
						{typed ? "이 매장인가요?" : "최근 간 곳으로 채우기"}{" "}
						<small>금액은 확인해 주세요</small>
					</p>
					<div>
						{matching.map((suggestion) => (
							<button
								key={suggestion.storeKey}
								type="button"
								onClick={() => {
									setStore(suggestion.storeDisplay);
									setAmount(String(suggestion.lastAmount));
									setCategoryId(suggestion.categoryId);
									setPinned(true);
									setItems([]);
									setDuplicateConfirmed(false);
								}}
							>
								{suggestion.storeDisplay}
								<small>
									{categoryName(suggestion.categoryId)} · 지난번{" "}
									{suggestion.lastAmount.toLocaleString("ko-KR")}원
								</small>
							</button>
						))}
					</div>
				</section>
			)}
			<label>
				매장 이름
				<input
					required
					maxLength={80}
					autoComplete="off"
					value={store}
					placeholder="예: 동네 카페"
					onChange={(event) => {
						setStore(event.target.value);
						setDuplicateConfirmed(false);
						if (!pinned) setCategoryId(inferCategory(event.target.value));
					}}
				/>
			</label>
			<label>
				총액
				<input
					required
					inputMode="numeric"
					autoComplete="off"
					value={amount}
					placeholder="0"
					onChange={(event) => {
						setAmount(event.target.value.replace(/[^0-9]/g, ""));
						setDuplicateConfirmed(false);
					}}
				/>
			</label>
			<label>
				분류
				<select
					aria-label="분류"
					aria-invalid={
						!categoryId && Boolean(store.trim()) ? "true" : undefined
					}
					value={categoryId}
					onChange={(event) => {
						setCategoryId(event.target.value);
						setPinned(true);
						setItems([]);
					}}
				>
					<option value="" disabled>
						분류 고르기
					</option>
					{categories.map((category) => (
						<option key={category.categoryId} value={category.categoryId}>
							{category.nameKo}
						</option>
					))}
				</select>
			</label>
			{isHomeItemCategory(categoryId) && (
				<details className="quick-items">
					<summary>산 물건도 남기기 (선택)</summary>
					<p className="field-hint">
						집 안 물건과 연결해요. 품목 가격이나 수량은 추측하지 않아요.
					</p>
					<div className="item-suggestions">
						{[
							...new Set([
								...household.items.map((item) => item.name),
								...household.shopping
									.filter((entry) => entry.status === "pending")
									.map((entry) => entry.name),
							]),
						]
							.slice(0, 8)
							.map((name) => (
								<button
									type="button"
									key={name}
									aria-pressed={items.includes(name)}
									onClick={() =>
										items.includes(name)
											? setItems(items.filter((item) => item !== name))
											: addItem(name)
									}
								>
									{name}
								</button>
							))}
					</div>
					<label>
						산 물건 이름
						<input
							maxLength={80}
							value={itemName}
							placeholder="예: 세탁 세제"
							onChange={(event) => setItemName(event.target.value)}
						/>
					</label>
					<button
						className="quiet-button"
						type="button"
						disabled={!itemName.trim()}
						onClick={() => addItem(itemName)}
					>
						품목 추가
					</button>
					<ul className="selected-items">
						{items.map((name) => (
							<li key={name}>
								{name}
								<button
									type="button"
									aria-label={`${name} 제외`}
									onClick={() =>
										setItems(items.filter((item) => item !== name))
									}
								>
									×
								</button>
							</li>
						))}
					</ul>
				</details>
			)}
			{duplicate && (
				<label className="duplicate-choice">
					<input
						type="checkbox"
						checked={duplicateConfirmed}
						onChange={(event) => setDuplicateConfirmed(event.target.checked)}
					/>
					별개 결제예요. 두 기록을 모두 저장할게요.
				</label>
			)}
			{error && (
				<p role="alert" className="form-error">
					{error}
				</p>
			)}
			<div className="quick-save-bar">
				<button
					className="button full-width"
					type="submit"
					disabled={busy || !store.trim() || !numericAmount || !categoryId}
					aria-busy={busy}
				>
					{busy
						? "저장 중…"
						: recurring
							? "실제 결제로 기록하기"
							: "이 금액으로 기록하기"}
				</button>
				<small>
					{recurring
						? "실제로 결제한 금액을 확인해 주세요"
						: "오늘 결제 · 이 기기에 저장"}
				</small>
			</div>
		</form>
	);
}
